/**
 * Authentication Client for Autodesk Platform Services
 * Handles OAuth2 authentication and token management
 */

import { httpClient } from "../core/http-client";
import { config, API_ENDPOINTS } from "../core/config";
import { AuthenticationError, ValidationError } from "../core/errors";

export interface TokenInfo {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at?: number;
}

export class AuthenticationClient {
  private tokenInfo: TokenInfo | null = null;
  private refreshPromise: Promise<TokenInfo> | null = null;

  /**
   * Get client credentials from config
   */
  private getCredentials() {
    const clientId = config.get("clientId");
    const clientSecret = config.get("clientSecret");

    if (!clientId || !clientSecret) {
      throw new ValidationError(
        "Missing APS credentials. Please check your .env file"
      );
    }

    return { clientId, clientSecret };
  }

  /**
   * Fetch new access token using client credentials
   */
  async fetchAccessToken(): Promise<TokenInfo> {
    const { clientId, clientSecret } = this.getCredentials();
    const scope = config.get("authScope");

    // Retry logic for APS authentication
    const maxAttempts = 3;
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await httpClient.post<TokenInfo>(
          API_ENDPOINTS.auth.token,
          new URLSearchParams({
            client_id: clientId,
            client_secret: clientSecret,
            grant_type: "client_credentials",
            scope: scope,
          }).toString(),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        this.tokenInfo = {
          ...response,
          expires_at: Date.now() + response.expires_in * 1000,
        };

        // Set token in HTTP client
        httpClient.setAccessToken(
          this.tokenInfo.access_token,
          this.tokenInfo.expires_in
        );

        return this.tokenInfo;
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxAttempts) {
          throw new AuthenticationError(
            `Failed to authenticate with Autodesk services after ${maxAttempts} attempts`,
            {
              originalError: error,
            }
          );
        }

        const delay = 1000 * Math.pow(2, attempt - 1); // 1s, 2s, 4s

        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Get current access token, fetching new one if needed
   */
  async getAccessToken(): Promise<string> {
    // Check if we have a valid token
    if (this.tokenInfo && this.tokenInfo.expires_at) {
      const now = Date.now();
      const buffer = 5 * 60 * 1000; // 5 minutes buffer

      if (now < this.tokenInfo.expires_at - buffer) {
        return this.tokenInfo.access_token;
      }
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      await this.refreshPromise;
      return this.tokenInfo!.access_token;
    }

    this.refreshPromise = this.fetchAccessToken();

    try {
      await this.refreshPromise;
      return this.tokenInfo!.access_token;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Set access token manually (e.g., from user input)
   */
  setAccessToken(token: string, expiresIn: number = 3600): void {
    this.tokenInfo = {
      access_token: token,
      token_type: "Bearer",
      expires_in: expiresIn,
      expires_at: Date.now() + expiresIn * 1000,
    };

    httpClient.setAccessToken(token, expiresIn);
  }

  /**
   * Clear stored authentication
   */
  clearAuthentication(): void {
    this.tokenInfo = null;
    this.refreshPromise = null;
    httpClient.clearToken();
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.tokenInfo || !this.tokenInfo.expires_at) {
      return true;
    }
    return Date.now() >= this.tokenInfo.expires_at;
  }

  /**
   * Get auth callback for Viewer initialization
   */
  getAuthCallback() {
    return async (
      callback: (token: string | null, expiresIn: number) => void
    ) => {
      try {
        const token = await this.getAccessToken();
        const expiresIn = this.tokenInfo?.expires_in || 3600;
        callback(token, expiresIn);
      } catch {
        callback(null, 0);
      }
    };
  }
}

// Create singleton instance
export const authClient = new AuthenticationClient();
